<script setup lang="ts">
  import vTooltip from '@leaflink/stash/directives/tooltip';
  import Icon from '@leaflink/stash/Icon.vue';
  import Image from '@leaflink/stash/Image.vue';
  import uniq from 'lodash-es/uniq';
  import { storeToRefs } from 'pinia';
  import { computed, ref } from 'vue';

  import LicenseTypeChip from '@/components/LicenseTypeChip/LicenseTypeChip.vue';
  import { Campaign } from '@/models/campaign';
  import { ProductCardProduct } from '@/models/shop';
  import PaymentsBadge from '@/pages/Shop/_components/PaymentsBadge.vue';
  import ToggleStoreFavorite from '@/pages/Shop/Purchasing/components/ToggleStoreFavorite.vue';
  import { useCompanyStore } from '@/piniaStores/company/company.store';
  import i18n from '@/plugins/i18n';
  import { getVarietyPriceRange } from '@/utils/commerce';
  import { money, percent } from '@/utils/i18n';

  import { hasBaseUnitPricing, hasSalePrice, hasVolumeDiscount, isParent } from '../utils';

  interface ProductCardProps {
    product: ProductCardProduct;
    campaign?: Campaign;
    isDeal?: boolean;
    isLazy?: boolean;
    isPublicMenu?: boolean;
    isSponsored?: boolean;
    isLegacySponsored?: boolean;
  }

  const props = defineProps<ProductCardProps>();

  const emit = defineEmits<{
    (event: 'click'): void;
  }>();

  const isFavorited = ref(!!props.product.isOnPurchaseList);

  const companyStore = useCompanyStore();

  const { activeCompany } = storeToRefs(companyStore);

  const discountString = computed(() => {
    const { children } = props.product;

    if (!children?.length) {
      // some products return their discount as a string value
      const numericDiscount = parseProductDiscount(props.product.discount);

      return percent(numericDiscount / 100);
    } else {
      const discounts = children.map((i) => parseProductDiscount(i.discount));
      const max = discounts.length ? Math.max(...discounts) : 0;
      const min = discounts.length ? Math.min(...discounts) : 0;
      let discountString = percent(min / 100);

      if (min !== max) {
        discountString += '+';
      }

      return discountString;
    }
  });

  const hasSameVarietyUnitsOfMeasure = computed(() => {
    return isParent(props.product) && uniq(props.product.children?.map((i) => i.unitOfMeasure)).length === 1;
  });

  const isActiveDeal = computed(() => {
    return props.campaign && isParent(props.product)
      ? props.product.children?.some((p) => p.isFeaturedOnDeals)
      : props.product.isFeaturedOnDeals;
  });

  const isProductAvailable = computed(() => props.product.shouldBeVisible);
  const isProductBackordered = computed(() => props.product.isBackordered);
  const isProductDiscounted = computed(() => !!props.product.salePrice?.amount);

  const productLicenseType = computed(() => props.product.license?.type || i18n.t('common.not_applicable_abbr'));

  const showTags = computed(
    () => (!props.isDeal && (isActiveDeal.value || hasSalePrice(props.product))) || hasVolumeDiscount(props.product),
  );

  const showProductStatusWarning = computed(() => !isProductAvailable.value || isProductBackordered.value);

  const showProductPricePerUnit = computed(() => !isParent(props.product) && props.product.unitMultiplier > 1);

  const showPaymentsBadge = computed(() => {
    if (!props.product.brandBadges) {
      return false;
    }

    return props.product.brandBadges.filter((badge) => badge === 'On Payments').length > 0;
  });

  const onProductClick = () => {
    emit('click');
  };

  const parseProductDiscount = (value: typeof props.product.discount) => {
    try {
      if (!value) {
        return 0;
      }

      return typeof value === 'string' ? parseFloat(value) : value;
    } catch {
      return 0;
    }
  };
</script>

<template>
  <li class="relative" :class="$style.card" data-test="component|productCard">
    <!-- Product Image -->
    <div :class="['relative', $style['image-wrapper']]">
      <Image
        :alt="$t('shop.featured_image')"
        :class="['absolute', $style.image]"
        :loading="isLazy ? 'lazy' : 'eager'"
        provider="cloudinary"
        :src="product.featuredImage"
        sizes="308px"
        @click="onProductClick"
      />
      <div class="absolute ll-pa-2 w-full flex align-top align-justify">
        <div class="flex align-top">
          <LicenseTypeChip data-test="component|licenseChip" :license="{ type: productLicenseType }" />
          <PaymentsBadge
            v-if="showPaymentsBadge"
            :class="$style['payments-badge']"
            :seller="product.brand"
            :tracking-data="{ page: 'Shop Products' }"
          />
        </div>
        <span
          v-if="isSponsored"
          class="inline-block ll-px-1 text-small rounded bg-ice-200 color-ice-700 font-weight-bold text-uppercase"
          :class="$style.sponsored"
          >{{ $t('shop.sponsored') }}</span
        >
      </div>
      <ToggleStoreFavorite
        v-if="!isSponsored"
        :is-favorited="isFavorited"
        :product="product"
        tracking-source="Shop Products"
        variant="over-image"
        :variant-options="{ position: 'top-right' }"
        @success="isFavorited = !isFavorited"
      />
    </div>

    <!-- Product Info -->
    <div class="flex flex-col ll-pt-2">
      <h4 v-if="!isPublicMenu && activeCompany" class="text-truncate text-small font-weight-normal ll-mb-1">
        <router-link
          class="color-ice-700"
          :to="{
            name: 'brand-menu',
            params: {
              brandId: product.brand.id,
              company: activeCompany.slug,
            },
          }"
        >
          {{ product.brand.name }}
        </router-link>
      </h4>
      <h3 class="heading-4 color-blue cursor-pointer ll-mb-1" data-test="product-card-name" @click="onProductClick">
        {{ product.displayName }}
      </h3>

      <slot name="pricing">
        <template v-if="!isPublicMenu">
          <div v-if="props.isDeal && isProductDiscounted" class="color-ice-700 ll-mb-1 overflow-hidden text-small">
            <Icon name="tag" :class="['color-teal', $style.icon]" />
            <span class="text-no-wrap">({{ $t('common.discountAmountOff', { amount: discountString }) }})</span>
          </div>
          <!-- Pricing -->
          <div class="flex flex-wrap heading-4 ll-mb-1 overflow-hidden">
            <!-- Tags -->
            <div v-if="showTags" class="flex ll-mr-1">
              <span
                v-if="isActiveDeal && campaign"
                v-tooltip.top="$t('shop.deals.featuredOnDeals', { campaignLabel: campaign.label })"
                data-test="component|dealsTag"
                :class="$style.tag"
              >
                <Icon name="tag-star" :class="['color-teal', $style.icon]" />
              </span>
              <span
                v-else-if="hasSalePrice(product)"
                v-tooltip.top="$t('shop.saleDiscountTooltip')"
                data-test="component|salePriceTag"
                :class="$style.tag"
              >
                <Icon name="tag" :class="['color-red', $style.icon]" />
              </span>
              <span
                v-if="hasVolumeDiscount(product)"
                v-tooltip.top="$t('shop.volumeDiscountTooltip')"
                data-test="component|volumeDiscountTag"
                :class="$style.tag"
              >
                <Icon name="tag" :class="['color-orange', $style.icon]" />
              </span>
            </div>

            <!-- Parent Product - Varieties Price Range -->
            <span v-if="isParent(product)" class="color-ice-900 text-lowercase" data-test="value|parentPricing">
              <!-- Same Units of Measure -->
              <template v-if="hasSameVarietyUnitsOfMeasure">
                {{ getVarietyPriceRange('effectivePrice', product) }}/{{ product.displayPriceUnit }}
              </template>
              <!-- Different Units of Measure -->
              <template v-else>
                {{ getVarietyPriceRange('effectivePrice', product) }}
              </template>
            </span>
            <!-- Standalone Item Pricing -->
            <template v-else>
              <!-- Sale Price (With Original Wholesale/Base Price) -->
              <template v-if="hasSalePrice(product)">
                <span class="color-ice text-strike-through ll-mr-1" data-test="value|saleBasePrice">
                  {{ money(product.basePrice) }}
                </span>
                <span class="color-ice-900 text-lowercase" data-test="value|saleEffectivePrice">
                  {{ money(product.salePrice) }}/{{ product.displayPriceUnit }}
                </span>
              </template>
              <!-- Wholesale/Base Price Only -->
              <span v-else class="color-ice-900 text-lowercase" data-test="value|basePrice">
                {{ money(product.basePrice) }}/{{ product.displayPriceUnit }}
              </span>
            </template>
          </div>

          <!-- Status Warnings -->
          <div v-if="showProductStatusWarning" class="text-small color-red" data-test="value|statusWarning">
            <template v-if="!isProductAvailable">{{ $t('common.currentlyUnavailable') }}</template>
            <template v-if="isProductBackordered">{{ $t('common.onBackorder') }}</template>
          </div>
          <!-- Unit Pricing -->
          <div v-else class="text-small text-lowercase">
            <!-- Price Per Unit In A Case -->
            <span v-if="showProductPricePerUnit" class="ll-mr-1" data-test="value|unitPricing">
              {{ money(product.pricePerUnit) }}/{{ $t('common.unit') }}
            </span>
            <!-- Price Per Base Unit -->
            <span v-if="hasBaseUnitPricing(product)" data-test="value|baseUnitPricing">
              <template v-if="isParent(product)">
                {{ getVarietyPriceRange('baseUnitPrice', product) }}/{{
                  product.subCategory.baseUnitOfMeasureShortName
                }}
              </template>
              <template v-else-if="product.subCategory && product.subCategory.baseUnitOfMeasureShortName">
                {{ money(product.baseUnitPrice) }}/{{ product.subCategory.baseUnitOfMeasureShortName }}
              </template>
            </span>
          </div>
        </template>
      </slot>
    </div>
  </li>
</template>

<style module lang="scss">
  .card {
    background: var(--color-white);
    list-style: none;
  }

  .image-wrapper {
    background: #f7f8f9;
    height: 0;
    padding-bottom: 100%;
  }

  .image {
    cursor: pointer;
    height: 100%;
    left: 0;
    object-fit: contain;
    top: 0;
    width: 100%;
  }

  .name {
    @include clamp(2);
  }

  .tag {
    display: inline-flex;
  }

  .icon {
    height: 16px;
    width: 16px;
  }

  // TODO: This treatment may become a pattern to convert to a component
  // (includes applied util classes)
  .sponsored {
    font-size: 0.625rem;
    line-height: 1;
    padding-bottom: 5px;
    padding-top: 5px;
  }

  .payments-badge {
    bottom: 0;
    height: 20px;
    left: 0;
  }
</style>
